/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, Row, Col, Button, Input } from 'reactstrap';
import useApi from 'vl-common/src/hooks/useApi';
import { useTranslation } from 'react-i18next';

interface ReasonSelectionModalProps {
  title?: string;
  titleDarkSide?: string;
  subText?: string;
  noBtnHandler: () => void;
  yesBtnHandler: (data: { key: string; value: string }) => void | Promise<void>;
  yesBtnText?: string;
  noBtnText?: string;
  onDone?: (() => void) | null;
  btnsStyle?: object;
  systemType?: string;
  reasonType: 'cancel' | 'reschedule';
  isOpen: boolean;
  toggle?: () => void;
}

const ReasonSelectionModal: React.FC<ReasonSelectionModalProps> = ({
  title,
  titleDarkSide = 'Appointment',
  subText,
  noBtnHandler,
  yesBtnHandler,
  yesBtnText = 'Confirm',
  noBtnText = 'Cancel',
  onDone = null,
  btnsStyle = {},
  systemType = '',
  reasonType,
  ...restProps
}) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const [selectedItem, setSelectedItem] = useState({ key: -1, value: '' });
  const [options, setOptions] = useState([]);
  const [inProgress, setInProgress] = useState(false);

  const config = {
    cancel: {
      defaultTitle: 'Cancel',
      defaultSubText: 'Please select a reason why you wish to cancel the appointment',
      reasonsKey: 'appt_cancel_reasons',
      codeKey: 'appt_cancel_code',
      descKey: 'appt_cancel_desc',
      testId: 'cancel-reason-select'
    },
    reschedule: {
      defaultTitle: 'Reschedule',
      defaultSubText: 'Please select a reason why you wish to reschedule the appointment',
      reasonsKey: 'appt_reschedule_reasons',
      codeKey: 'appt_reschedule_code',
      descKey: 'appt_reschedule_desc',
      testId: 'reschedule-reason-select'
    }
  };

  const currentConfig = config[reasonType];
  const finalTitle = title || currentConfig.defaultTitle;
  const finalSubText = subText || currentConfig.defaultSubText;

  useEffect(() => {
    Api.meta
      .getUserAppointmentMeta()
      .then((res) => {
        const { data } = res;
        const { meta } = data;
        const reasons = meta[0][currentConfig.reasonsKey];
        const opt = reasons?.map((item) => {
          return {
            reason_code: item[currentConfig.codeKey],
            description: item[currentConfig.descKey]
          };
        });
        setOptions(opt);
      })
      .catch((e) => {});
  }, []);

  useEffect(() => {
    if (restProps.isOpen) {
      setSelectedItem({ key: -1, value: '' });
    }
  }, [restProps.isOpen]);

  const onReasonChanged = (e) => {
    let key = e.target.value;

    if (key == -1) {
      //don't change it to === comparison
      setSelectedItem({ key: -1, value: '' });
      return;
    }

    const values = options.filter(function (item) {
      return item.reason_code == e.target.value;
    });
    let value = values[0].description;

    setSelectedItem({ key: key, value: value });
  };

  return (
    <Modal size="lg" {...restProps} centered>
      <ModalBody>
        <Row>
          <Col className="vl-flex-col-center">
            <div style={styles.title}>
              {finalTitle}
              {titleDarkSide.trim().length > 0 ? <span style={styles.titleDarkSide}>{` ${titleDarkSide}`}</span> : null}
            </div>
            {finalSubText !== '' && <div style={styles.subText}>{finalSubText}</div>}
            <div className="vl-select">
              <Input
                className="minimal"
                type="select"
                style={styles.select}
                value={selectedItem.key}
                onChange={onReasonChanged}
                placeholder={t('Select from dropdown')}
                data-testid={currentConfig.testId}
              >
                <option value={-1}>{t('Select from dropdown')}</option>
                {options?.map(function (data) {
                  return (
                    <option key={data.reason_code} value={data.reason_code}>
                      {data.description}
                    </option>
                  );
                })}
              </Input>
            </div>
            <div className="vl-flex-row-center" style={styles.btnsContainer}>
              {noBtnText !== '' && (
                <Button style={{ ...styles.btnGray, ...btnsStyle }} type="button" onClick={noBtnHandler}>
                  {noBtnText}
                </Button>
              )}
              {yesBtnText !== '' && (
                <Button
                  style={{ ...styles.btnMain, ...btnsStyle }}
                  type="button"
                  disabled={selectedItem.key === -1 || inProgress}
                  onClick={async () => {
                    setInProgress(true);
                    await yesBtnHandler(selectedItem);
                    setInProgress(false);
                  }}
                >
                  {yesBtnText}
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export { ReasonSelectionModal };

const styles = {
  btnsContainer: { marginBottom: '30px' },
  btnMain: {
    width: '110px',
    height: '40px',
    backgroundColor: 'var(--productPrimary)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productPrimary)',
    color: 'var(--productPrimaryDark)'
  },
  btnGray: {
    width: '110px',
    height: '40px',
    backgroundColor: 'var(--productGray)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productGray)',
    marginRight: '10px'
  },
  title: { color: 'var(--productPink)', fontSize: '30px', fontWeight: 'bold' },
  titleDarkSide: { color: 'var(--productPrimaryDark)', fontWeight: 'bold' },
  subText: {
    color: 'var(--productPrimaryDark)',
    fontSize: '14px',
    marginTop: '30px',
    marginBottom: '30px'
  },
  select: {
    width: '400px',
    marginBottom: '30px'
  }
};
